import { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  QrCode, 
  Download, 
  Copy, 
  Check, 
  Share2, 
  X,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

// Get the site URL - handle both with and without /api suffix
const getSiteUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (backendUrl.endsWith('/api')) {
    return backendUrl.slice(0, -4);
  }
  if (backendUrl) {
    return backendUrl;
  }
  return window.location.origin;
};

const SITE_URL = getSiteUrl();

export default function QRCodeShare({ 
  variant = 'button', // 'button', 'inline', 'card'
  size = 'md', // 'sm', 'md', 'lg'
  showDownload = true,
  className = ''
}) {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  const shareUrl = SITE_URL;
  
  const qrSizes = {
    sm: 120,
    md: 180,
    lg: 250
  };

  const handleCopyLink = async () => {
    const textToCopy = shareUrl;
    
    // Method 1: Try modern clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        // Continue to fallback
      }
    }
    
    // Method 2: Fallback using textarea
    const textArea = document.createElement('textarea');
    textArea.value = textToCopy;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (err) {
      document.body.removeChild(textArea);
    }
    
    // Method 3: If all else fails, show the link for manual copy
    toast.info('Please select and copy the link manually');
  };

  const handleDownload = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;

    // Create download link from canvas
    const pngUrl = canvas.toDataURL('image/png');
    const downloadLink = document.createElement('a');
    downloadLink.download = 'susu-money-qr.png';
    downloadLink.href = pngUrl;
    downloadLink.click();
    
    toast.success('QR code downloaded!');
  };

  const handleShare = async () => {
    // Check if Web Share API is available (mainly mobile devices)
    if (navigator.share && navigator.canShare) {
      try {
        await navigator.share({
          title: 'Join Susu Money',
          text: 'Save together, grow together! Join the global Susu savings community.',
          url: shareUrl
        });
        toast.success('Shared successfully!');
      } catch (err) {
        // User cancelled or error - fall back to copy
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      // Web Share not available - copy link instead
      handleCopyLink();
      toast.info('Link copied! Share it on your favorite platform.');
    }
  };

  // Inline QR code display
  if (variant === 'inline') {
    return (
      <div className={`flex flex-col items-center ${className}`} ref={qrRef}>
        <div className="bg-white p-4 rounded-2xl shadow-lg">
          <QRCodeCanvas
            value={shareUrl}
            size={qrSizes[size]}
            level="H"
            includeMargin={true}
            imageSettings={{
              src: "/logo192.png",
              height: 30,
              width: 30,
              excavate: true,
            }}
          />
        </div>
        <p className="text-xs text-stone-500 mt-2 text-center">
          Scan to join Susu Money
        </p>
        {showDownload && (
          <div className="flex gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="rounded-full text-xs"
              data-testid="qr-download-btn"
            >
              <Download className="w-3 h-3 mr-1" />
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyLink}
              className="rounded-full text-xs"
              data-testid="qr-copy-btn"
            >
              {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Card variant with full details
  if (variant === 'card') {
    return (
      <div className={`bg-gradient-to-br from-indigo-50 to-amber-50 rounded-2xl p-6 ${className}`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-stone-900 flex items-center">
              <QrCode className="w-5 h-5 mr-2 text-indigo-600" />
              Share with Friends
            </h3>
            <p className="text-sm text-stone-600 mt-1">
              Scan or share this QR code to invite others
            </p>
          </div>
          <Sparkles className="w-5 h-5 text-amber-500" />
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div ref={qrRef} className="bg-white p-4 rounded-xl shadow-md">
            <QRCodeCanvas
              value={shareUrl}
              size={qrSizes[size]}
              level="H"
              includeMargin={true}
              fgColor="#4F46E5"
            />
          </div>
          
          <div className="flex-1 space-y-3">
            <div className="bg-white/70 rounded-xl p-3">
              <p className="text-xs text-stone-500 mb-1">Share link</p>
              <p className="text-sm font-mono text-stone-700 truncate">{shareUrl}</p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleShare}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="qr-share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button
                variant="outline"
                onClick={handleDownload}
                className="rounded-full"
                data-testid="qr-download-card-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyLink}
                className="rounded-full"
                data-testid="qr-copy-card-btn"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: Button that opens modal
  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowModal(true)}
        className={`rounded-full ${className}`}
        data-testid="qr-open-btn"
      >
        <QrCode className="w-4 h-4 mr-2" />
        QR Code
      </Button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
            onClick={() => setShowModal(false)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '16px' }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-4 sm:p-5 max-w-xs w-full shadow-2xl relative scale-[0.9]"
              onClick={(e) => e.stopPropagation()}
              data-testid="qr-modal"
            >
              {/* Close button - always visible at top right */}
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 hover:bg-red-100 text-stone-600 hover:text-red-600 transition-colors z-10"
                data-testid="qr-close-btn"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center mb-3 pr-8">
                <h2 className="text-base font-bold text-stone-900 flex items-center">
                  <QrCode className="w-5 h-5 mr-1.5 text-indigo-600" />
                  Share Susu Money
                </h2>
              </div>

              <div ref={qrRef} className="flex justify-center mb-3">
                <div className="bg-gradient-to-br from-indigo-100 to-amber-100 p-3 rounded-xl">
                  <QRCodeCanvas
                    value={shareUrl}
                    size={140}
                    level="H"
                    includeMargin={true}
                    fgColor="#4F46E5"
                  />
                </div>
              </div>

              <p className="text-center text-stone-600 mb-3 text-xs">
                Scan to join the Susu community
              </p>

              <div 
                className="bg-stone-50 rounded-lg p-2 mb-3 cursor-pointer hover:bg-stone-100 transition-colors"
                onClick={handleCopyLink}
                title="Click to copy link"
              >
                <p className="text-[10px] text-stone-500 mb-0.5">Link (click to copy)</p>
                <p className="text-xs font-mono text-indigo-600 break-all select-all">{shareUrl}</p>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Button
                  onClick={handleShare}
                  size="sm"
                  className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-xs py-2"
                  data-testid="qr-modal-share-btn"
                >
                  <Share2 className="w-3 h-3 mr-1" />
                  Share
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  className="rounded-lg text-xs py-2"
                  data-testid="qr-modal-download-btn"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className="rounded-lg text-xs py-2"
                  data-testid="qr-modal-copy-btn"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
